/**
 * Composable for managing game history and recent games
 *
 * Provides functionality for:
 * - Tab state management (actions, recent, history)
 * - Fetching recent resumable games
 * - Fetching historical games with pagination
 * - Fetching game details for modal view
 */

import { ref, computed } from 'vue'
import type {
  GameSummary,
  GameDetailResponse,
  RecentGamesResponse,
  HistoricalGamesResponse,
  LobbyTab
} from '@/types/game'

// API URL configuration (same pattern as GameLobby)
const isDevPort = window.location.port && !['80', '443', ''].includes(window.location.port)
const API_URL = (window.location.hostname === 'localhost' && isDevPort)
  ? `${window.location.protocol}//localhost:8000/api`
  : `${window.location.origin}/api`

// Shared state (module-level singleton)
const activeTab = ref<LobbyTab>('actions')
const recentGames = ref<GameSummary[]>([])
const historicalGames = ref<GameSummary[]>([])
const selectedGame = ref<GameDetailResponse | null>(null)

const isLoadingRecent = ref(false)
const isLoadingHistory = ref(false)
const isLoadingDetail = ref(false)

const historyPage = ref(1)
const historyTotalCount = ref(0)
const HISTORY_PER_PAGE = 10

export function useGameHistory() {
  const hasMoreHistory = computed(() => {
    return historyPage.value * HISTORY_PER_PAGE < historyTotalCount.value
  })

  const recentGamesCount = computed(() => recentGames.value.length)

  /**
   * Get auth token from localStorage
   */
  function getAuthToken(): string | null {
    return localStorage.getItem('access_token')
  }

  /**
   * Fetch user's recent resumable games
   */
  async function fetchRecentGames(): Promise<void> {
    const token = getAuthToken()
    if (!token) {
      console.warn('[useGameHistory] No auth token, skipping recent games fetch')
      return
    }

    isLoadingRecent.value = true
    try {
      const response = await fetch(`${API_URL}/games/my-games/recent`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data: RecentGamesResponse = await response.json()
        recentGames.value = data.games
        console.log(`[useGameHistory] Fetched ${data.games.length} recent games`)
      } else if (response.status === 401) {
        console.warn('[useGameHistory] Auth token expired')
        recentGames.value = []
      } else {
        console.error('[useGameHistory] Failed to fetch recent games:', response.status)
      }
    } catch (error) {
      console.error('[useGameHistory] Error fetching recent games:', error)
    } finally {
      isLoadingRecent.value = false
    }
  }

  /**
   * Fetch user's historical games with pagination
   */
  async function fetchHistoricalGames(page = 1): Promise<void> {
    const token = getAuthToken()
    if (!token) {
      console.warn('[useGameHistory] No auth token, skipping history fetch')
      return
    }

    isLoadingHistory.value = true
    try {
      const response = await fetch(
        `${API_URL}/games/my-games/history?page=${page}&per_page=${HISTORY_PER_PAGE}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const data: HistoricalGamesResponse = await response.json()
        if (page === 1) {
          historicalGames.value = data.games
        } else {
          // Append for "load more" pagination
          historicalGames.value = [...historicalGames.value, ...data.games]
        }
        historyTotalCount.value = data.total_count
        historyPage.value = page
        console.log(`[useGameHistory] Fetched page ${page} of history (${data.games.length} games, ${data.total_count} total)`)
      } else if (response.status === 401) {
        console.warn('[useGameHistory] Auth token expired')
        historicalGames.value = []
      } else {
        console.error('[useGameHistory] Failed to fetch history:', response.status)
      }
    } catch (error) {
      console.error('[useGameHistory] Error fetching history:', error)
    } finally {
      isLoadingHistory.value = false
    }
  }

  /**
   * Fetch detailed game info for the game detail modal
   */
  async function fetchGameDetail(gameId: string): Promise<void> {
    const token = getAuthToken()
    if (!token) {
      console.warn('[useGameHistory] No auth token, cannot fetch game detail')
      return
    }

    isLoadingDetail.value = true
    try {
      const response = await fetch(
        `${API_URL}/games/my-games/${gameId}/detail`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        selectedGame.value = await response.json()
        console.log(`[useGameHistory] Fetched details for game ${gameId}`)
      } else {
        console.error('[useGameHistory] Failed to fetch game detail:', response.status)
        selectedGame.value = null
      }
    } catch (error) {
      console.error('[useGameHistory] Error fetching game detail:', error)
      selectedGame.value = null
    } finally {
      isLoadingDetail.value = false
    }
  }

  /**
   * Set active tab and fetch data if needed
   */
  function setTab(tab: LobbyTab): void {
    activeTab.value = tab

    // Lazy load data when tab is first selected
    if (tab === 'recent' && recentGames.value.length === 0 && !isLoadingRecent.value) {
      fetchRecentGames()
    } else if (tab === 'history' && historicalGames.value.length === 0 && !isLoadingHistory.value) {
      fetchHistoricalGames()
    }
  }

  /**
   * Load more historical games
   */
  function loadMoreHistory(): void {
    if (hasMoreHistory.value && !isLoadingHistory.value) {
      fetchHistoricalGames(historyPage.value + 1)
    }
  }

  /**
   * Close game detail modal
   */
  function closeGameDetail(): void {
    selectedGame.value = null
  }

  /**
   * Refresh all game data
   */
  async function refreshAll(): Promise<void> {
    const token = getAuthToken()
    if (!token) return

    // Reset pagination
    historyPage.value = 1

    // Fetch both in parallel
    await Promise.all([
      fetchRecentGames(),
      fetchHistoricalGames(1)
    ])
  }

  /**
   * Remove a game from the recent list (after resuming)
   */
  function removeFromRecent(gameId: string): void {
    recentGames.value = recentGames.value.filter(g => g.id !== gameId)
  }

  /**
   * Delete a single game from recent games (calls API)
   */
  async function deleteRecentGame(gameId: string): Promise<boolean> {
    const token = getAuthToken()
    if (!token) {
      console.warn('[useGameHistory] No auth token, cannot delete game')
      return false
    }

    try {
      const response = await fetch(`${API_URL}/games/my-games/${gameId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        // Remove from local state
        recentGames.value = recentGames.value.filter(g => g.id !== gameId)
        console.log(`[useGameHistory] Deleted game ${gameId}`)
        return true
      } else {
        console.error('[useGameHistory] Failed to delete game:', response.status)
        return false
      }
    } catch (error) {
      console.error('[useGameHistory] Error deleting game:', error)
      return false
    }
  }

  /**
   * Delete all recent games (calls API for each)
   */
  async function deleteAllRecentGames(): Promise<boolean> {
    const token = getAuthToken()
    if (!token) {
      console.warn('[useGameHistory] No auth token, cannot delete games')
      return false
    }

    try {
      const response = await fetch(`${API_URL}/games/my-games/recent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        // Clear local state
        recentGames.value = []
        console.log('[useGameHistory] Deleted all recent games')
        return true
      } else {
        console.error('[useGameHistory] Failed to delete all games:', response.status)
        return false
      }
    } catch (error) {
      console.error('[useGameHistory] Error deleting all games:', error)
      return false
    }
  }

  /**
   * Format relative time (e.g., "2 hours ago", "3 days ago")
   */
  function formatTimeAgo(dateString: string): string {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  /**
   * Format game status for display
   */
  function formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      waiting: 'Waiting',
      in_progress: 'In Progress',
      paused: 'Paused',
      finished: 'Finished'
    }
    return statusMap[status] || status
  }

  return {
    // State
    activeTab,
    recentGames,
    historicalGames,
    selectedGame,
    isLoadingRecent,
    isLoadingHistory,
    isLoadingDetail,
    hasMoreHistory,
    recentGamesCount,

    // Actions
    setTab,
    fetchRecentGames,
    fetchHistoricalGames,
    fetchGameDetail,
    loadMoreHistory,
    closeGameDetail,
    refreshAll,
    removeFromRecent,
    deleteRecentGame,
    deleteAllRecentGames,

    // Helpers
    formatTimeAgo,
    formatStatus
  }
}
